import React, { useEffect, useState } from 'react';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	FormControlLabel,
	Radio,
	RadioGroup,
	TextField,
	Checkbox,
	IconButton,
	Button,
} from '@mui/material';
import PrimaryButton from '../button/PrimaryButton';
import Text from '../text';
import ApiService from 'utils/services/ApiService';
import LoadingPopup from '../dialog/LoadingDeleteAccount';
import { useMsal } from '@azure/msal-react';

interface DeleteConfirmationDialogProps {
	open: boolean;
	onClose: () => void;
	onConfirm: (reason: string, customReason?: string) => void;
}

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
	open,
	onClose,
	onConfirm,
}) => {
	const [dialogStage, setDialogStage] = useState(1); 
	const [selectedReason, setSelectedReason] = useState('');
	const [customReason, setCustomReason] = useState('');
	const [agreementChecked, setAgreementChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const { instance } = useMsal();

	const reasons = [
		'I have stopped running ATP tests',
		'Technical issues, the app does not function as I expect',
		'Too challenging to learn how to use',
		'I have found another solution',
		'Another reason',
	];

	useEffect(() => {
		if (!open) {
			setDialogStage(1);
			setSelectedReason('');
			setCustomReason('');
			setAgreementChecked(false);
		}
	}, [open]);

  const handleConfirm = async () => {
    if (dialogStage === 1) {
      setDialogStage(2);
    } else if (dialogStage === 2 && agreementChecked) {
      setLoading(true);
      const email = localStorage.getItem('currentUser') ?? 'unknown';
      const deletionReason = selectedReason === 'Another reason' ? customReason : selectedReason;
      const data = {
        status: "Active",
        email: email,
        deletionReason: deletionReason || 'User skipped the reason'
      };
      
      try {
        await ApiService.deleteAccount(data);
        setTimeout(() => {
          setLoading(false);
          instance.logoutRedirect({
            postLogoutRedirectUri: "/"
          });
        }, 2000);
      } catch (error) {
        console.error('Error deleting account:', error);
        setLoading(false);
      }
     
    }
  };

	const handleSkip = () => {
		setDialogStage(2);
	};

	const isContinueEnabled =
		(dialogStage === 1 &&
			(selectedReason !== '' || customReason.trim() !== '')) ||
		(dialogStage === 2 && agreementChecked);

	const renderFirstStage = () => (
		<DialogContent>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<Text addStyle="!font-inter !font-bold !text-[#2C2C2C] !text-[24px] !mb-4">
					Delete Account
				</Text>
				<IconButton
					onClick={onClose}
					sx={{ p: 0, mr: '5px', mt: '2px' }}
					className="absolute top-[-8px] right-0 close-main-menu"
				>
					<img
						src="/assets/icons/Close.svg"
						alt="Remove"
						className="w-5 h-5 md:block hidden"
					/>
				</IconButton>
			</div>
			<DialogContentText className=" mb-[10px]">
				<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px]">
					We're sad to see you go! Please tell us why you
					want to permanently delete your account. Your feedback will
					help us improve the app and further support our users.
				</Text>
			</DialogContentText>
			<RadioGroup
				name="delete-reason"
				value={selectedReason}
				className="mt-[10px]"
				onChange={(event) => setSelectedReason(event.target.value)}
			>
				{reasons.map((reason, index) => (
					<FormControlLabel
						key={index}
						value={reason}
						control={
							<Radio
								sx={{
									'&.Mui-checked': {
										color: '#0093C8',
									},
								}}
							/>
						}
						label={reason}
						sx={{
							'& .MuiTypography-root': {
								fontFamily: 'Jost, sans-serif',
								color: '#2C2C2C',
								fontSize: '16px',
							},
						}}
					/>
				))}
			</RadioGroup>

			<TextField
				placeholder="Another reason"
				multiline
				rows={2}
				variant="outlined"
				value={customReason}
				onChange={(event) => setCustomReason(event.target.value)}
				sx={{
					mb: 2,
					width: '300px',
					height: '80px',
					ml: '30px',
					borderColor:
						selectedReason === 'Another reason'
							? '#0093C8'
							: '#ccc',
					'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
						{
							borderColor:
								selectedReason === 'Another reason'
									? '#0093C8'
									: '#ccc',
						},
				}}
				disabled={selectedReason !== 'Another reason'}
			/>
		</DialogContent>
	);

	const renderSecondStage = () => (
		<DialogContent
			style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
		>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<Text addStyle="!font-inter !font-bold !text-[#2C2C2C] !text-[24px] !mb-4">
					Delete Account
				</Text>
				<IconButton
					onClick={onClose}
					sx={{ p: 0, mr: '5px', mt: '2px' }}
					className="absolute top-[-8px] right-0 close-main-menu"
				>
					<img
						src="/assets/icons/Close.svg"
						alt="Remove"
						className="w-5 h-5 md:block hidden"
					/>
				</IconButton>
			</div>
			<DialogContentText style={{ flexGrow: 1, overflowY: 'auto' }}>
				<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px]">
					Deleting your account is permanent. If you proceed, you will no longer be able to log in, 
					restore your account, or retrieve any of your account data. 
					LuminUltra will keep a record of your
					request to delete your account and primary information in
					accordance with our legal data retention obligations as indicated
					by our Privacy Policy.
				</Text>
			</DialogContentText>

			<FormControlLabel
				control={
					<Checkbox
						checked={agreementChecked}
						onChange={(e) => setAgreementChecked(e.target.checked)}
						sx={{
							'&.Mui-checked': {
								color: '#0093C8',
							},
						}}
					/>
				}
				label="Yes, I want to permanently delete my account."
				style={{ alignSelf: 'flex-start', marginTop: 'auto' }}
			/>
		</DialogContent>
	);

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
					onClose();
				}
			}}
			fullWidth
			maxWidth="sm"
			id="main-menu"
			sx={{
				'& .MuiPaper-root': {
					overflow: 'hidden',
					maxHeight: '82vh',
					minHeight: '600px',
					position: 'absolute',
					top: '50px',
				},
			}}
		>
{dialogStage === 1 ? (
  <>
    {renderFirstStage()}
    <DialogActions className="mt-[10px] mb-[10px] mr-[10px]">
      {dialogStage === 1 && (
        <Button onClick={handleSkip} color="primary" className="!text-[#9EACB4]">
          SKIP
        </Button>
      )}

      <PrimaryButton onClick={handleConfirm} disabled={!isContinueEnabled}>
        Continue
      </PrimaryButton>
    </DialogActions>
  </>
) : (
  <>
    {renderSecondStage()}
    <DialogActions className="mt-[10px] mb-[10px] mr-[10px]">
      {dialogStage === 1 && (
        <Button onClick={handleSkip} color="primary" className="!text-[#9EACB4]">
          SKIP
        </Button>
      )}

      <PrimaryButton onClick={handleConfirm} disabled={!isContinueEnabled}>
        Delete Account
      </PrimaryButton>
    </DialogActions>
  </>
)}
      {loading && <LoadingPopup open={loading} />}
		</Dialog>
    
	);
};

export default DeleteConfirmationDialog;
